/**
 * FloatingButtons Presentational Component
 * For more informations about this kind of component, read README
 *
 * References:
 *  - https://usehooks.com/useWindowSize/
 */

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  FiMenu, FiArrowLeft, FiCrosshair, FiPlus, FiMinus,
} from 'react-icons/fi';

import { setSidebarVisible } from '../../../../../support/store/SidebarReducer';
import styles from './FloatingButtons.module.css';

/**
 * useFloatingButtons UI logic hook.
 * Enable eventsHandlres to control UI behaviour and localStates to represent UI states.
 * @param {Commands} commands
 * @returns local states and events handles
 */
export function useFloatingButtons(commands) {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [windowWidth, setWindowWidth] = useState(null);

  const handleZoomIn = () => commands.zoomIn();
  const handleZoomOut = () => commands.zoomOut();
  const handleGoBack = () => history.goBack();
  const handleSideMenu = () => dispatch(setSidebarVisible({ visible: true }));
  const handleRecentralize = () => commands.locateUser();

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return {
    localStates: {
      windowWidth,
      location,
    },
    eventsHandlers: {
      handleZoomIn,
      handleZoomOut,
      handleGoBack,
      handleSideMenu,
      handleRecentralize,
    },
  };
}

/**
 * FloatingButtons purely presentational component
 * Get user events and pass it to UI hook
 * @returns JSX presentational component
 */
export default function FloatingButtons(props) {
  const { sharedStates, commands } = props;
  const { localStates, eventsHandlers } = useFloatingButtons(commands);

  const buttonsColor = '#AAA';

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {
          localStates?.location?.state?.previousPath &&
          (
            <section className={styles['go-back-button']}>
              <button className={styles.button} type="button" onClick={eventsHandlers.handleGoBack}>
                <FiArrowLeft size={32} strokeWidth="1.5" color={buttonsColor} />
              </button>
            </section>
          )
        }

        <section>
          <button
            role="menu"
            className={styles.button}
            type="button"
            onClick={eventsHandlers.handleSideMenu}
          >
            <FiMenu size={32} strokeWidth="1.5" color={buttonsColor} />
          </button>

          { !sharedStates.popupOpened || localStates.windowWidth > 470 ? (
            <div className={styles['map-controls']}>
              <button
                className={styles.button}
                type="button"
                onClick={eventsHandlers.handleZoomIn}
              >
                <FiPlus size={32} strokeWidth="1.5" color={buttonsColor} />
              </button>

              <button className={styles.button} type="button" onClick={eventsHandlers.handleZoomOut}>
                <FiMinus size={32} strokeWidth="1.5" color={buttonsColor} />
              </button>

              <button className={styles.button} type="button" onClick={eventsHandlers.handleRecentralize}>
                <FiCrosshair size={32} strokeWidth="1.5" color={buttonsColor} />
              </button>
            </div>
          ) : <></>}
        </section>
      </div>
    </div>
  );
}
